import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import TaskRow from "../components/TaskRow";
import TaskForm from "../components/TaskForm";

/**
 * Matrice Urgente/Importante (Eisenhower):
 *   Urgente & Importante  → priority = critical
 *   Importante Non urgente → priority = high
 *   Urgente Non importante → priority = medium
 *   Non urgente Non imp.  → priority = low
 */
const QUADRANTS = [
  { key: "critical", title: "Urgente e importante", subtitle: "Fai subito", cls: "bg-red-50 border-red-200" },
  { key: "high",     title: "Importante, non urgente", subtitle: "Pianifica", cls: "bg-blue-50 border-blue-200" },
  { key: "medium",   title: "Urgente, non importante", subtitle: "Delega", cls: "bg-yellow-50 border-yellow-200" },
  { key: "low",      title: "Né urgente né importante", subtitle: "Elimina", cls: "bg-gray-50 border-gray-200" },
];

export default function TasksMatrix() {
  const [tasks, setTasks] = useState([]);
  const [areas, setAreas] = useState([]);
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);

  const load = async () => {
    try {
      const [t, a] = await Promise.all([api.listTasks({ include_completed: false }), api.listAreas()]);
      setTasks(t); setAreas(a);
    } catch { toast.error("Errore"); }
  };
  useEffect(() => { load(); }, []);

  const areaById = useMemo(() => Object.fromEntries(areas.map((a) => [a.id, a])), [areas]);
  const byQ = useMemo(() => {
    const g = { critical: [], high: [], medium: [], low: [] };
    for (const t of tasks) g[t.priority || "medium"].push(t);
    return g;
  }, [tasks]);

  const complete = async (t) => {
    try { await api.completeTask(t.id); toast.success("✓ Completato"); load(); }
    catch { toast.error("Errore"); }
  };

  return (
    <div className="space-y-6" data-testid="matrix-page">
      <div>
        <div className="text-xs font-mono uppercase tracking-widest opacity-70">Eisenhower</div>
        <h1 className="mt-1 font-display text-3xl sm:text-4xl font-semibold">Matrice Urgente / Importante</h1>
        <p className="mt-2 text-sm opacity-80 max-w-2xl">Le tue attività organizzate per priorità operativa.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {QUADRANTS.map((q) => (
          <div key={q.key} className={`rounded-2xl border p-4 ${q.cls}`} data-testid={`matrix-quadrant-${q.key}`}>
            <div className="mb-2">
              <div className="text-xs font-mono uppercase tracking-widest opacity-70">{q.subtitle}</div>
              <div className="font-display text-lg font-semibold">{q.title}</div>
            </div>
            <div className="space-y-1">
              {byQ[q.key].length === 0 && <div className="text-xs opacity-60 py-2">Vuoto.</div>}
              {byQ[q.key].map((t) => (
                <TaskRow key={t.id} task={t} area={areaById[t.area_id]}
                         onComplete={complete}
                         onEdit={(x) => { setEditing(x); setFormOpen(true); }}
                         showPriority={false} />
              ))}
            </div>
          </div>
        ))}
      </div>
      <TaskForm open={formOpen} initial={editing} onClose={() => setFormOpen(false)} onSaved={load} />
    </div>
  );
}
